import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Bot,
  Boxes,
  CheckCircle2,
  CircuitBoard,
  Cpu,
  FileText,
  GitBranch,
  History,
  Library,
  Moon,
  Network,
  Package,
  ScanSearch,
  Settings,
  Shield,
  Sparkles,
  Sun,
  TableProperties,
  Users,
  Wifi,
  WifiOff,
  Workflow,
} from 'lucide-react';
import AuthModal from '../components/auth/AuthModal';
import Button from '../components/common/Button';
import { useUIStore } from '../shared/lib/uiStore';

const modules = [
  {
    icon: <CircuitBoard className="h-5 w-5" />,
    title: 'PLM workspace',
    description: 'Manage circuit designs, lifecycle states, current revisions, and product metadata from concept through production.',
  },
  {
    icon: <FileText className="h-5 w-5" />,
    title: 'PDM workspace',
    description: 'Track engineering documents and build bill-of-materials structures with quantities, costs, and reference designators.',
  },
  {
    icon: <Package className="h-5 w-5" />,
    title: 'ERP workspace',
    description: 'Monitor component inventory, supplier purchase orders, and cost-engineering views used by operations teams.',
  },
  {
    icon: <GitBranch className="h-5 w-5" />,
    title: 'ECM workspace',
    description: 'Capture change requests, change orders, risk, approvals, target dates, and design revision history.',
  },
];

const implementationNotes = [
  'Dashboard with metrics, process guidance, and activity visualization',
  'Supabase authentication with sign-up, sign-in, and password reset',
  'Admin, settings, command palette, contextual help, and AI side panel shell',
  'Installable PWA setup with manifest and service worker registration',
];

const workflowTracks = [
  {
    icon: <Workflow className="h-5 w-5" />,
    title: 'Lifecycle control',
    description: 'Track products from concept through prototype, production, and end-of-life while preserving revision history and ownership.',
  },
  {
    icon: <TableProperties className="h-5 w-5" />,
    title: 'Spreadsheet-first PDM',
    description: 'Support the real electronics workflow where BOMs, AVL rollups, test matrices, and release travelers still move through Excel.',
  },
  {
    icon: <Boxes className="h-5 w-5" />,
    title: 'Inventory and sourcing',
    description: 'Connect part usage, stock position, supplier risk, and purchasing signals so engineering decisions are grounded in supply reality.',
  },
  {
    icon: <GitBranch className="h-5 w-5" />,
    title: 'Change governance',
    description: 'Handle requests, approvals, impact, and release effects through explicit ECM flows rather than scattered comments and spreadsheets.',
  },
];

const detailSections = [
  {
    icon: <Library className="h-5 w-5" />,
    title: 'What ElectroPLM is',
    body: 'ElectroPLM is an electronics-oriented lifecycle workspace that combines product definition, PDM, ERP-adjacent inventory and supply visibility, and change management in one shell. It is aimed at teams building boards, modules, and systems that need traceability across design, sourcing, release, and sustaining engineering.',
  },
  {
    icon: <Network className="h-5 w-5" />,
    title: 'Why the ontology matters',
    body: 'The AI layer is being shaped around a PLM ontology rather than generic keyword search. That means the assistant is intended to reason about products, BOMs, components, documents, revisions, changes, suppliers, and inventory as related objects, not isolated records.',
  },
  {
    icon: <ScanSearch className="h-5 w-5" />,
    title: 'What the system should answer',
    body: 'The goal is to answer practical engineering questions such as where a part is used, what a change affects, whether a release is blocked, which documents support a BOM revision, and which sourcing issues threaten a product line.',
  },
];

const aiCapabilities = [
  'GraphWiki-backed ontology pages for PLM concepts, workflows, and roadmap decisions',
  'Deterministic query routing between ontology, operational PLM data, and external web evidence',
  'Feature request intake directly inside chat, with a prioritized backlog maintained in the app',
  'Hybrid FAQ cache using local browser storage plus shared Supabase-backed storage',
];

const viewerHighlights = [
  'Browser-native PDF review for controlled release artifacts',
  'Excel-oriented handling for BOM imports, cost sheets, travelers, and test matrices',
  'Native EDA and CAD tool expectations made explicit instead of implied',
  'Deployment-time viewer manifest so the app can advertise what is browser-ready versus workstation-native',
];

const roadmapPreview = [
  'Role-aware assistant guardrails for approval and governance-sensitive workflows',
  'Evidence and citations from PLM records plus GraphWiki pages',
  'Release readiness copilots that combine BOM, document, change, and inventory state',
  'Server-side conversion services for richer browser previews of engineering artifacts',
];

const stats = [
  { value: '4', label: 'Core modules', icon: <Cpu className="h-4 w-4" /> },
  { value: '7+', label: 'Primary workspaces', icon: <Users className="h-4 w-4" /> },
  { value: 'Auth', label: 'Protected access', icon: <Shield className="h-4 w-4" /> },
  { value: 'PWA', label: 'Installable shell', icon: <Wifi className="h-4 w-4" /> },
];

const StartupPage: React.FC = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [authError, setAuthError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const theme = useUIStore((state) => state.theme);
  const toggleTheme = useUIStore((state) => state.toggleTheme);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('auth_error');

    if (errorParam) {
      setAuthError(decodeURIComponent(errorParam));
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (
      !supabaseUrl ||
      !supabaseAnonKey ||
      supabaseUrl === 'your_supabase_project_url' ||
      supabaseAnonKey === 'your_supabase_anon_key' ||
      supabaseUrl.includes('placeholder')
    ) {
      if (import.meta.env.PROD) {
        setConfigError('Supabase configuration is missing. Please contact support.');
      } else {
        setConfigError('Supabase environment variables are not configured. Please check your .env file.');
      }
    }
  }, []);

  const handleAuthClick = (mode: 'signin' | 'signup') => {
    if (configError) return;
    setAuthMode(mode);
    setShowAuthModal(true);
    setAuthError(null);
  };

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const authUnavailable = Boolean(authError || configError);

  return (
    <div className="min-h-screen bg-surface text-content transition-colors duration-300">
      {configError && (
        <div className="border-b border-danger-500/20 bg-danger-50/90 px-4 py-3 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center gap-3">
            <WifiOff className="h-5 w-5 flex-shrink-0 text-danger-500" />
            <div className="flex-1">
              <p className="text-sm text-danger-500">
                <strong>Configuration Error:</strong> {configError}
              </p>
              {import.meta.env.DEV && (
                <p className="mt-1 text-xs text-danger-500/90">
                  Create a .env file with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from your Supabase project.
                </p>
              )}
            </div>
            <button onClick={() => setConfigError(null)} className="text-danger-500 transition-colors hover:text-danger-900">
              <span className="sr-only">Dismiss</span>
              ×
            </button>
          </div>
        </div>
      )}

      {authError && (
        <div className="border-b border-danger-500/20 bg-danger-50/90 px-4 py-3 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-danger-500" />
            <div className="flex-1">
              <p className="text-sm text-danger-500">
                <strong>Authentication Error:</strong> {authError}
              </p>
            </div>
            <button onClick={() => setAuthError(null)} className="text-danger-500 transition-colors hover:text-danger-900">
              <span className="sr-only">Dismiss</span>
              ×
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto flex min-h-screen max-w-7xl flex-col px-4 py-4 sm:px-6 lg:px-8">
        <nav className="glass-panel sticky top-4 z-20 mb-8 flex h-16 items-center justify-between rounded-[28px] px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-[20px] bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-card">
              <Cpu className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xl font-semibold tracking-[-0.05em] text-content">ElectroPLM</p>
              <p className="text-xs text-content-muted">Electronics workflows that match the current implementation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface-raised/80 text-content-secondary transition-all duration-200 hover:bg-surface-overlay hover:text-content"
              aria-label={`Switch to ${theme === 'dark' ? 'day' : 'night'} mode`}
              title={theme === 'dark' ? 'Switch to day mode' : 'Switch to night mode'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection('overview')}
              className="text-content-secondary hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-surface-overlay"
            >
              Overview
            </Button>
            <Button variant="primary" onClick={() => scrollToSection('workflows')}>
              Explore Details
            </Button>
          </div>
        </nav>

        <section id="overview" className="grid gap-6 pb-10 pt-4 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pt-10">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-surface-raised/80 px-4 py-2 text-sm text-content-secondary backdrop-blur-xl shadow-subtle">
              <Sparkles className="h-4 w-4 text-secondary-500" />
              Built around electronics lifecycle workflows.
            </div>

            <h1 className="apple-title max-w-4xl text-5xl font-semibold tracking-tight text-content sm:text-6xl lg:text-7xl">
              ElectroPLM is a detailed electronics lifecycle workspace, not just a generic dashboard shell.
            </h1>

            <p className="apple-subtitle mt-6 max-w-3xl text-lg leading-8 text-content-secondary sm:text-xl">
              It brings together PLM, PDM, ERP-adjacent inventory and sourcing views, ECM change control, and an ontology-driven assistant
              aimed at electronics programs where BOM accuracy, release evidence, and part risk all matter at once.
            </p>

            {authUnavailable ? (
              <div className="mt-6 rounded-[26px] border border-warning-500/25 bg-warning-500/8 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning-500" />
                  <div>
                    <p className="text-sm font-semibold text-content">Live sign-in is currently unavailable</p>
                    <p className="mt-1 text-sm leading-6 text-content-secondary">
                      The public page below still explains the system in detail. When connectivity is restored, sign-in can unlock the live product records and workflows.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={() => scrollToSection('ai')}
                icon={<ArrowRight className="h-4 w-4" />}
              >
                See AI And Ontology
              </Button>
              <Button variant="outline" size="lg" onClick={() => scrollToSection('viewer-stack')}>
                See Viewer Strategy
              </Button>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat) => (
                <div key={stat.label} className="glass-panel rounded-[24px] px-4 py-4">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-primary-500/10 text-primary-500">
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-semibold tracking-tight text-content">{stat.value}</div>
                  <div className="mt-1 text-sm text-content-muted">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-[32px] p-6 sm:p-8">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="apple-kicker">System Summary</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-content">What ElectroPLM covers</h2>
              </div>
              <div className="rounded-full border border-border bg-surface-overlay px-3 py-1 text-xs text-content-muted">
                PWA ready
              </div>
            </div>
            <div className="space-y-3">
              {detailSections.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-[22px] border border-border bg-surface-overlay/70 px-4 py-4">
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-500/10 text-primary-500">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-content">{item.title}</p>
                    <p className="mt-1 text-sm leading-6 text-content-secondary">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-[24px] border border-border bg-primary-500/8 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-500/12 text-primary-500">
                  <Bot className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-content">Ontology-aware assistant is part of the product direction</p>
                  <p className="text-xs text-content-muted">The AI path is intended to combine GraphWiki meaning, live PLM records, graph reasoning, and external search only when needed.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="workflows" className="pb-16">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="apple-kicker">Core modules</p>
              <h2 className="text-3xl font-semibold tracking-tight text-content">Representative of the current implementation</h2>
            </div>
            <div className="hidden rounded-full border border-border bg-surface-raised/80 px-4 py-2 text-sm text-content-muted backdrop-blur-xl md:block">
              Admin, settings, command palette, and help are already wired into the shell.
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {modules.map((module) => (
              <div key={module.title} className="glass-panel rounded-[28px] p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-[20px] bg-primary-500/10 text-primary-500">
                  {module.icon}
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-content">{module.title}</h3>
                <p className="mt-3 text-sm leading-6 text-content-secondary">{module.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-16">
          <div className="mb-6">
            <p className="apple-kicker">Workflow Tracks</p>
            <h2 className="text-3xl font-semibold tracking-tight text-content">How the product is meant to be used</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {workflowTracks.map((track) => (
              <div key={track.title} className="glass-panel rounded-[28px] p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-[20px] bg-secondary-500/10 text-secondary-500">
                  {track.icon}
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-content">{track.title}</h3>
                <p className="mt-3 text-sm leading-6 text-content-secondary">{track.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="ai" className="pb-16">
          <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="glass-panel rounded-[32px] p-6 sm:p-8">
              <p className="apple-kicker">AI Layer</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-content">Ontology-first assistant design</h2>
              <p className="mt-4 text-sm leading-7 text-content-secondary">
                ElectroPLM is not aiming for a generic chatbot bolted on top of records. The direction is an assistant that understands lifecycle objects,
                knows how they connect, and can separate internal truth from external evidence.
              </p>
            </div>

            <div className="grid gap-3">
              {aiCapabilities.map((item) => (
                <div key={item} className="glass-panel rounded-[26px] px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-2xl bg-primary-500/10 text-primary-500">
                      <Network className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-content-secondary">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="viewer-stack" className="pb-16">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="glass-panel rounded-[32px] p-6 sm:p-8">
              <p className="apple-kicker">Viewer Strategy</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-content">Built for real PDM review surfaces</h2>
              <p className="mt-4 text-sm leading-7 text-content-secondary">
                Electronics PDM only works if the system acknowledges how teams actually review artifacts: PDFs in-browser, spreadsheets in Excel, and source design files in native EDA or CAD tools.
              </p>
            </div>
            <div className="space-y-3">
              {viewerHighlights.map((item) => (
                <div key={item} className="glass-panel rounded-[26px] px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-2xl bg-secondary-500/10 text-secondary-500">
                      <FileText className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-content-secondary">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-10">
          <div className="glass-panel rounded-[32px] p-6 sm:p-8">
            <p className="apple-kicker">Roadmap Snapshot</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-content">What comes next after the current shell</h2>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {roadmapPreview.map((item) => (
                <div key={item} className="rounded-[24px] border border-border bg-surface-overlay/60 px-4 py-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
                    <p className="text-sm leading-6 text-content-secondary">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-10">
          <div className="glass-panel flex flex-col gap-6 rounded-[32px] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-semibold tracking-tight text-content">This page is the public product brief</h2>
              <p className="mt-2 text-sm leading-6 text-content-secondary">
                You do not need to log in just to understand ElectroPLM. When auth is available, the same shell can open into the internal engineering workspaces and live records.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" size="lg" onClick={() => scrollToSection('overview')}>
                Back To Top
              </Button>
              <Button variant="secondary" size="lg" onClick={() => handleAuthClick('signin')} disabled={!!configError}>
                Sign In
              </Button>
            </div>
          </div>
        </section>

        <footer className="mt-auto border-t border-border/70 px-2 py-6 text-sm text-content-muted">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p>ElectroPLM</p>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-2">
                <History className="h-4 w-4" />
                Revision-aware
              </span>
              <span className="inline-flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Configurable shell
              </span>
            </div>
          </div>
        </footer>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} initialMode={authMode} />
    </div>
  );
};

export default StartupPage;